import React, { useState, useEffect } from "react";
import {
  Upload,
  FileText,
  Save,
  CheckCircle,
  AlertCircle,
  Loader2,
  X,
  MessageSquare,
  Code,
  Copy,
  Calendar,
  User,
  Car,
  Search,
  Eye,
  Database,
  Home,
  Zap,
  Shield,
  AlertTriangle,
  Info,
  Edit,
  RefreshCw,
  Cpu,
  Table,
} from "lucide-react";

// Mock Router components for demonstration
const Router = ({ children }) => <div>{children}</div>;
const Routes = ({ children }) => {
  const [currentPath, setCurrentPath] = React.useState('/');
  
  React.useEffect(() => {
    window.__navigate = setCurrentPath;
  }, []);
  
  const childArray = React.Children.toArray(children);
  const matchedRoute = childArray.find(child => {
    if (child.props.path === '*') return true;
    return child.props.path === currentPath;
  });
  
  return matchedRoute || childArray.find(child => child.props.path === '*');
};
const Route = ({ element }) => element;
const Link = ({ to, children, className }) => (
  <a 
    href={to} 
    className={className}
    onClick={(e) => {
      e.preventDefault();
      if (window.__navigate) window.__navigate(to);
    }}
  >
    {children}
  </a>
);
const useLocation = () => {
  const [pathname, setPathname] = React.useState('/');
  
  React.useEffect(() => {
    const originalNavigate = window.__navigate;
    window.__navigate = (path) => {
      setPathname(path);
      if (originalNavigate) originalNavigate(path);
    };
  }, []);
  
  return { pathname };
};

// --- API Configuration ---
const API_BASE_URL = "/api";

// --- Field Configuration for Extraction Display ---
const FIELD_CONFIG = {
  "NOS REFERENCES": { label: "Nos Références", icon: FileText, backendField: "nos_references" },
  "DATE DE MISSION": { label: "Date de Mission", icon: Calendar, backendField: "date_mission" },
  "MANDANT": { label: "Mandant", icon: User, backendField: "mandant" },
  "AGENCE": { label: "Agence", icon: Home, backendField: "agence" },
  "DATE ACCIDENT": { label: "Date Accident", icon: Calendar, backendField: "date_accident" },
  "ASSURE": { label: "Assuré(e)", icon: User, backendField: "assure" },
  "TELEPHONE ASSURE": { label: "Téléphone Assuré", icon: MessageSquare, backendField: "telephone_assure" },
  "IMMATRICULATION": { label: "Immatriculation", icon: Car, backendField: "immatriculation" },
  "EXPERTISE TYPE": { label: "Type d'Expertise", icon: Code, backendField: "expertise_type" },
  "ADRESSE EXPERTISE": { label: "Adresse Expertise", icon: Home, backendField: "adresse_expertise" },
};

// --- Available Models ---
const AVAILABLE_MODELS = [
  {
    value: "gemini-2.5-flash-lite",
    label: "Gemini 2.0 Flash Lite",
    provider: "gemini"
  },
  {
    value: "qwen/qwen2.5-vl-32b-instruct:free", 
    label: "Qwen 2.5 VL 32B",
    provider: "qwen"
  }
];

// --- Helper function to get readable label from key ---
const getFieldLabel = (key) => {
  const config = Object.values(FIELD_CONFIG).find(
    (field) => field.backendField === key.toLowerCase().replace(/ /g, '_')
  );
  return config ? config.label : key.replace(/_/g, ' ');
};

// ------------------------------------------------
// --- NAVIGATION & UTILITIES ---
// ------------------------------------------------

function Navigation() {
  const location = useLocation();
  const navItems = [
    { name: "Extraction Dossier", path: "/", icon: Upload },
    { name: "Extraction Tableaux", path: "/tables", icon: Table },
    { name: "Liste des Dossiers", path: "/records", icon: Database },
  ];

  return (
    <nav className="bg-white shadow-md sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-blue-600">
              <Zap className="inline-block w-6 h-6 mr-1" /> Auto-Expertise AI
            </h1>
          </div>
          <div className="flex space-x-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? "bg-blue-500 text-white shadow-lg"
                    : "text-gray-600 hover:bg-gray-100 hover:text-blue-600"
                }`}
              >
                <item.icon className="w-4 h-4 mr-2" />
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
}

// ------------------------------------------------
// --- UPLOAD AND EXTRACTION PAGE ---
// ------------------------------------------------

function UploadPage() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [extractionResult, setExtractionResult] = useState(null);
  const [fullJsonResponse, setFullJsonResponse] = useState(null);
  const [activeTab, setActiveTab] = useState('data');
  const [dataCopied, setDataCopied] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [selectedModel, setSelectedModel] = useState("gemini-2.0-flash-lite");

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setError("");
    setExtractionResult(null);
    setFullJsonResponse(null);
    setSaveSuccess(false);
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Veuillez sélectionner un fichier avant de lancer l'extraction.");
      return;
    }

    setLoading(true);
    setError("");
    setExtractionResult(null);
    setFullJsonResponse(null);
    setActiveTab('data');
    setSaveSuccess(false);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const selectedModelConfig = AVAILABLE_MODELS.find(model => model.value === selectedModel);
      const response = await fetch(
        `${API_BASE_URL}/upload?model_provider=${selectedModelConfig.provider}&model_name=${selectedModelConfig.value}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();
      setFullJsonResponse(result);

      if (response.ok) {
        setExtractionResult(result.data);
        setEditedData(result.data);
        setIsEditing(false);
      } else {
        setError(result.detail || "Erreur inconnue lors de l'extraction.");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError("Une erreur réseau est survenue lors de l'envoi du fichier.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!fullJsonResponse) return;

    setSaveLoading(true);
    setSaveSuccess(false);
    setError("");

    try {
        const dataToSave = {};
        
        const reverseLabelMapping = {
          "NOS REFERENCES": "nos_references",
          "DATE DE MISSION": "date_mission",
          "MANDANT": "mandant",
          "AGENCE": "agence",
          "Date Accident": "date_accident",
          "Assuré(e)": "assure",
          "Tiers": "tiers",
          "Contrat": "contrat",
          "Contrat 2": "contrat_2",
          "N° Dossier": "numero_dossier",
          "CIE Adv.": "cie_adv",
          "Immatriculation": "immatriculation",
          "Immatriculation Véh. Tiers": "immatriculation_tiers",
          "Date d'examen": "date_examen",
          "Véh. Expertisé": "vehicule_expertise",
          "VA:": "va",
          "Lieu d'examen": "lieu_examen",
          "Observation": "observation",
          "Marque": "marque",
          "Genre": "genre",
          "N° Série": "numero_serie",
          "Type": "type_vehicule",
          "Couleur": "couleur",
          "Date 1° MC": "date_premiere_mc",
          "Puissance": "puissance",
          "Etat Véh.": "etat_vehicule",
          "Circonstance": "circonstance",
          "Index Km": "index_km",
          "Energie": "energie",
          "VN": "vn",
          "VV": "vv",
          "NATURE DES CHOC ET DES DEGATS": "nature_chocs_degats"
        };
        
        Object.entries(editedData).forEach(([frenchLabel, value]) => {
          const backendField = reverseLabelMapping[frenchLabel];
          if (backendField) {
            dataToSave[backendField] = value;
          }
        });
        
        const payload = {
            record_id: fullJsonResponse.record_id,
            transcription: fullJsonResponse.transcription,
            ...dataToSave
        };
        
        const response = await fetch(`${API_BASE_URL}/save`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });
        
        if (response.ok) {
            setSaveSuccess(true);
            setExtractionResult(null);
            setFullJsonResponse(null);
            setFile(null);
            setIsEditing(false);
            setEditedData({});
        } else {
            const result = await response.json();
            setError(result.detail || "Erreur inconnue lors de l'enregistrement.");
        }
    } catch (err) {
        console.error("Save error:", err);
        setError("Une erreur réseau est survenue lors de l'enregistrement.");
    } finally {
        setSaveLoading(false);
    }
  };

  const handleFieldChange = (key, value) => {
    setEditedData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCancelEdit = () => {
    setEditedData(extractionResult);
    setIsEditing(false);
  };

  const copyToClipboard = (text, type) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      if (type === 'data') {
        setDataCopied(true);
        setTimeout(() => setDataCopied(false), 2000);
      } else {
        setJsonCopied(true);
        setTimeout(() => setJsonCopied(false), 2000);
      }
    });
  };

  const copyDataToClipboard = () => {
    if (!editedData) return;
    const textToCopy = Object.entries(editedData)
      .map(([key, value]) => `${getFieldLabel(key)}: ${value}`)
      .join('\n');
    copyToClipboard(textToCopy, 'data');
  };

  const copyJsonToClipboard = () => {
    if (!fullJsonResponse) return;
    const textToCopy = JSON.stringify(fullJsonResponse, null, 2);
    copyToClipboard(textToCopy, 'json');
  };

  return (
    <div className="p-6 bg-gray-50 min-h-[calc(100vh-64px)]">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-gray-800 flex items-center gap-3">
          <Upload className="w-7 h-7 text-blue-600" />
          Extraction et Enregistrement de Dossiers
        </h1>

        {/* Upload Card */}
        <div className="bg-white shadow-xl border border-gray-200 rounded-xl p-8 mb-8">
          {/* Model Selection */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <label className="block text-sm font-medium text-blue-700 mb-3 flex items-center gap-2">
              <Cpu className="w-4 h-4" />
              Sélection du Modèle IA
            </label>
            <div className="space-y-3">
              {AVAILABLE_MODELS.map((model) => (
                <div key={model.value} className="flex items-center">
                  <input
                    type="radio"
                    id={model.value}
                    name="model"
                    value={model.value}
                    checked={selectedModel === model.value}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label htmlFor={model.value} className="ml-3 block text-sm font-medium text-gray-700">
                    {model.label}
                  </label>
                </div>
              ))}
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Modèle sélectionné: <span className="font-medium">
                {AVAILABLE_MODELS.find(m => m.value === selectedModel)?.label}
              </span>
            </div>
          </div>

          <label
            htmlFor="file-upload"
            className={`block w-full text-center py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              file ? 'border-green-400 bg-green-50' : 'border-blue-300 hover:border-blue-500 bg-gray-50'
            }`}
          >
            {file ? (
              <div className="text-green-700 font-semibold flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5" /> Fichier sélectionné: {file.name}
              </div>
            ) : (
              <div className="text-blue-600 font-semibold">
                <FileText className="w-6 h-6 mx-auto mb-2" />
                Cliquez pour choisir un fichier d'expertise (PDF, JPG, PNG)
              </div>
            )}
            <input id="file-upload" type="file" onChange={handleFileChange} className="hidden" accept=".pdf,.jpg,.jpeg,.png" />
          </label>

          <button
            onClick={handleUpload}
            disabled={!file || loading}
            className="w-full mt-6 flex items-center justify-center gap-3 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors shadow-md hover:shadow-lg text-lg"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin w-5 h-5" />
                Extraction en cours...
              </>
            ) : (
              <>
                <Zap className="w-5 h-5" />
                Lancer l'Extraction
              </>
            )}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-lg font-medium flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>Erreur: {error}</span>
          </div>
        )}

        {/* Results Card */}
        {extractionResult && (
          <div className="bg-white shadow-xl border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h2 className="text-2xl font-bold text-green-700 flex items-center gap-2">
                <CheckCircle className="w-6 h-6" /> Extraction Réussie
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                  {AVAILABLE_MODELS.find(m => m.value === selectedModel)?.provider === 'gemini' ? 'Gemini' : 'Qwen'}
                </span>
                <button
                  onClick={() => {
                    setExtractionResult(null);
                    setIsEditing(false);
                    setEditedData({});
                  }}
                  className="text-gray-500 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-red-50"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-6">
              <button
                onClick={() => setActiveTab('data')}
                className={`px-4 py-2 font-medium text-sm transition-colors ${
                  activeTab === 'data' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Données Structurées
              </button>
              <button
                onClick={() => setActiveTab('json')}
                className={`px-4 py-2 font-medium text-sm transition-colors ${
                  activeTab === 'json' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Réponse JSON
              </button>
            </div>

            {/* Tab Content */}
            <div>
              {/* Tab: Structured Data */}
              {activeTab === 'data' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Champs Extraits</h2>
                    <div className="flex gap-2">
                      {!isEditing ? (
                        <>
                          <button 
                            onClick={() => setIsEditing(true)} 
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                          >
                            <Edit className="w-4 h-4" /> Modifier
                          </button>
                          <button 
                            onClick={copyDataToClipboard} 
                            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium"
                          >
                            <Copy className="w-4 h-4" /> {dataCopied ? "Copié!" : "Copier"}
                          </button>
                        </>
                      ) : (
                        <button 
                          onClick={handleCancelEdit} 
                          className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium"
                        >
                          <X className="w-4 h-4" /> Annuler
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {Object.entries(editedData)
                      .filter(([key]) => key !== 'transcription') 
                      .map(([key, value]) => {
                        const label = getFieldLabel(key);
                        const Icon = Object.values(FIELD_CONFIG).find(
                          (field) => field.backendField === key.toLowerCase().replace(/ /g, '_')
                        )?.icon || MessageSquare;
                        
                        return (
                          <div key={key} className={`p-4 rounded-lg border shadow-sm ${isEditing ? 'bg-white border-blue-300' : 'bg-blue-50 border-blue-200'}`}>
                            <div className="flex items-center text-sm font-bold text-blue-700 mb-1">
                              <Icon className="w-4 h-4 mr-2" /> {label}
                            </div>
                            {isEditing ? (
                              <input
                                type="text"
                                value={value || ''}
                                onChange={(e) => handleFieldChange(key, e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800 font-medium text-base"
                              />
                            ) : (
                              <p className="text-gray-800 font-medium text-base break-words">{value || 'N/A'}</p>
                            )}
                          </div>
                        );
                      })}
                  </div>
                  
                  {fullJsonResponse?.transcription && (
                    <div className="mt-6">
                      <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-gray-600" /> Transcription brute
                      </h3>
                      <div className="bg-gray-100 p-4 rounded-lg text-sm text-gray-700 max-h-48 overflow-y-auto border border-gray-200">
                        <p className="whitespace-pre-wrap">{fullJsonResponse.transcription}</p>
                      </div>
                    </div>
                  )}

                  <div className="mt-8 text-center">
                    {saveSuccess ? (
                      <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg font-semibold shadow-lg">
                        <CheckCircle className="w-5 h-5" /> Dossier enregistré avec succès !
                      </div>
                    ) : (
                      <button
                        onClick={handleSave}
                        disabled={saveLoading || !fullJsonResponse}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors shadow-lg text-lg"
                      >
                        {saveLoading ? (
                          <>
                            <Loader2 className="animate-spin w-5 h-5" /> Enregistrement...
                          </>
                        ) : (
                          <>
                            <Save className="w-5 h-5" /> Enregistrer le Dossier
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Tab: Raw JSON */}
              {activeTab === 'json' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Réponse JSON Complète</h2>
                    {fullJsonResponse && (
                      <button onClick={copyJsonToClipboard} className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium">
                        <Copy className="w-4 h-4" /> {jsonCopied ? "Copié!" : "Copier JSON"}
                      </button>
                    )}
                  </div>
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-auto max-h-96 shadow-lg">
                    <pre className="font-mono">
                      {JSON.stringify(fullJsonResponse, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------
// --- TABLE EXTRACTION PAGE ---
// ------------------------------------------------

function TableExtractionPage() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tableResult, setTableResult] = useState(null);
  const [selectedModel, setSelectedModel] = useState("gemini-2.0-flash-lite");
  const [copied, setCopied] = useState(false);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setError("");
    setTableResult(null);
  };

  const extractTable = async () => {
    if (!file) {
      setError("Veuillez sélectionner un fichier avant de lancer l'extraction.");
      return;
    }

    setLoading(true);
    setError("");
    setTableResult(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const selectedModelConfig = AVAILABLE_MODELS.find(model => model.value === selectedModel);
      const response = await fetch(
        `${API_BASE_URL}/extract-table?model_provider=${selectedModelConfig.provider}&model_name=${selectedModelConfig.value}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (response.ok) {
        setTableResult(result);
      } else {
        setError(result.detail || "Erreur inconnue lors de l'extraction des tableaux.");
      }
    } catch (err) {
      console.error("Table extraction error:", err);
      setError("Une erreur réseau est survenue lors de l'envoi du fichier.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!tableResult?.tables) return;
    const textToCopy = JSON.stringify(tableResult.tables, null, 2);
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatTableData = (tables) => {
    if (!tables || tables.length === 0) return "Aucun tableau détecté";

    return tables.map((table, tableIndex) => (
      <div key={tableIndex} className="mb-8">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-bold text-gray-800">
            Tableau {tableIndex + 1} {table.title ? `- ${table.title}` : ''}
          </h3>
          <span className="text-sm text-gray-500">
            {table.rows?.length || 0} lignes × {table.columns?.length || 0} colonnes
          </span>
        </div>
        
        {table.rows && table.rows.length > 0 ? (
          <div className="overflow-x-auto border border-gray-300 rounded-lg">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  {table.columns?.map((col, colIndex) => (
                    <th 
                      key={colIndex} 
                      className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300"
                    >
                      {col || `Colonne ${colIndex + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, rowIndex) => (
                  <tr 
                    key={rowIndex} 
                    className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    {row.map((cell, cellIndex) => (
                      <td 
                        key={cellIndex} 
                        className="px-4 py-3 text-sm text-gray-800 border-b border-gray-200"
                      >
                        {cell || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
            Tableau détecté mais structure non reconnue
          </div>
        )}
        
        {table.raw_text && (
          <div className="mt-4 p-3 bg-gray-100 rounded text-sm text-gray-700">
            <span className="font-medium">Texte brut :</span> {table.raw_text}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="p-6 bg-gray-50 min-h-[calc(100vh-64px)]">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-gray-800 flex items-center gap-3">
          <Table className="w-7 h-7 text-blue-600" />
          Extraction de Tableaux
        </h1>

        {/* Upload Card */}
        <div className="bg-white shadow-xl border border-gray-200 rounded-xl p-8 mb-8">
          {/* Model Selection */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <label className="block text-sm font-medium text-blue-700 mb-3 flex items-center gap-2">
              <Cpu className="w-4 h-4" />
              Sélection du Modèle IA
            </label>
            <div className="space-y-3">
              {AVAILABLE_MODELS.map((model) => (
                <div key={model.value} className="flex items-center">
                  <input
                    type="radio"
                    id={`table-${model.value}`}
                    name="table-model"
                    value={model.value}
                    checked={selectedModel === model.value}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label htmlFor={`table-${model.value}`} className="ml-3 block text-sm font-medium text-gray-700">
                    {model.label}
                  </label>
                </div>
              ))}
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Modèle sélectionné: <span className="font-medium">
                {AVAILABLE_MODELS.find(m => m.value === selectedModel)?.label}
              </span>
            </div>
          </div>

          <label
            htmlFor="table-file-upload"
            className={`block w-full text-center py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              file ? 'border-green-400 bg-green-50' : 'border-blue-300 hover:border-blue-500 bg-gray-50'
            }`}
          >
            {file ? (
              <div className="text-green-700 font-semibold flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5" /> Fichier sélectionné: {file.name}
              </div>
            ) : (
              <div className="text-blue-600 font-semibold">
                <FileText className="w-6 h-6 mx-auto mb-2" />
                Cliquez pour choisir un document contenant des tableaux (PDF, JPG, PNG)
              </div>
            )}
            <input 
              id="table-file-upload" 
              type="file" 
              onChange={handleFileChange} 
              className="hidden" 
              accept=".pdf,.jpg,.jpeg,.png" 
            />
          </label>

          <button
            onClick={extractTable}
            disabled={!file || loading}
            className="w-full mt-6 flex items-center justify-center gap-3 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors shadow-md hover:shadow-lg text-lg"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin w-5 h-5" />
                Extraction des tableaux en cours...
              </>
            ) : (
              <>
                <Table className="w-5 h-5" />
                Extraire les Tableaux
              </>
            )}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-lg font-medium flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>Erreur: {error}</span>
          </div>
        )}

        {/* Results Card */}
        {tableResult && (
          <div className="bg-white shadow-xl border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between border-b pb-4 mb-6">
              <h2 className="text-2xl font-bold text-green-700 flex items-center gap-2">
                <CheckCircle className="w-6 h-6" /> Extraction des Tableaux Réussie
              </h2>
              <div className="flex items-center gap-3">
                <div className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                  {AVAILABLE_MODELS.find(m => m.value === selectedModel)?.provider === 'gemini' ? 'Gemini' : 'Qwen'}
                </div>
                {tableResult.tables && (
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium"
                  >
                    <Copy className="w-4 h-4" /> {copied ? "Copié!" : "Copier JSON"}
                  </button>
                )}
                <button
                  onClick={() => {
                    setTableResult(null);
                    setFile(null);
                  }}
                  className="text-gray-500 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-red-50"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Table Count Summary */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3 bg-white rounded-lg shadow-sm border border-blue-200">
                  <div className="text-sm font-medium text-blue-700">Nombre de tableaux</div>
                  <div className="text-2xl font-bold text-blue-800">
                    {tableResult.tables?.length || 0}
                  </div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm border border-blue-200">
                  <div className="text-sm font-medium text-blue-700">Modèle utilisé</div>
                  <div className="text-lg font-semibold text-blue-800">
                    {AVAILABLE_MODELS.find(m => m.value === selectedModel)?.label}
                  </div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm border border-blue-200">
                  <div className="text-sm font-medium text-blue-700">Temps de traitement</div>
                  <div className="text-lg font-semibold text-blue-800">
                    {tableResult.processing_time ? `${tableResult.processing_time}s` : 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Extracted Tables */}
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Tableaux Extraits</h3>
              {tableResult.tables && tableResult.tables.length > 0 ? (
                formatTableData(tableResult.tables)
              ) : (
                <div className="p-6 bg-yellow-50 border border-yellow-300 rounded-lg text-center">
                  <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                  <p className="text-lg font-medium text-yellow-700">Aucun tableau n'a pu être extrait du document</p>
                  <p className="text-yellow-600 mt-2">Essayez avec un autre document ou un autre modèle</p>
                </div>
              )}
            </div>

            {/* Raw JSON View */}
            <div className="mt-8 border-t pt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">Données JSON Brutes</h3>
                <span className="text-sm text-gray-500">Pour débogage uniquement</span>
              </div>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-auto max-h-80 shadow-lg">
                <pre className="font-mono">
                  {JSON.stringify(tableResult, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------
// --- RECORDS PAGE ---
// ------------------------------------------------

function RecordsPage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [fraudAnalysis, setFraudAnalysis] = useState(null);
  const [fraudLoading, setFraudLoading] = useState(false);
  const [fraudModelProvider, setFraudModelProvider] = useState("gemini");

  // Fonction pour récupérer tous les dossiers
  const fetchRecords = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/records`);
      if (response.ok) {
        const result = await response.json();
        setRecords(result.records || []);
      } else {
        setError("Erreur lors du chargement des dossiers");
      }
    } catch (err) {
      console.error("Fetch records error:", err);
      setError("Erreur réseau lors du chargement des dossiers");
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour analyser la fraude
  const analyzeFraud = async (recordId) => {
    setFraudLoading(true);
    setFraudAnalysis(null);
    try {
      const response = await fetch(`${API_BASE_URL}/detect-fraud/${recordId}?model_provider=${fraudModelProvider}`, {
        method: "POST",
      });
      if (response.ok) {
        const result = await response.json();
        setFraudAnalysis(result);
      } else {
        setError("Erreur lors de l'analyse de fraude");
      }
    } catch (err) {
      console.error("Fraud analysis error:", err);
      setError("Erreur réseau lors de l'analyse de fraude");
    } finally {
      setFraudLoading(false);
    }
  };

  // Charger les dossiers au montage du composant
  useEffect(() => {
    fetchRecords();
  }, []);

  // Réinitialiser l'analyse de fraude quand on ouvre un nouveau dossier
  useEffect(() => {
    if (selectedRecord) {
      setFraudAnalysis(null);
    }
  }, [selectedRecord]);

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Fonction pour obtenir la couleur du niveau de risque
  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case "élevé":
        return "text-red-600 bg-red-100";
      case "moyen":
        return "text-orange-600 bg-orange-100";
      case "faible":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-[calc(100vh-64px)]">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Database className="w-8 h-8 text-blue-600" />
            Liste des Dossiers Expertise
          </h1>
          <button
            onClick={fetchRecords}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Chargement..." : "Actualiser"}
          </button>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="p-4 mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-lg font-medium flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Tableau des dossiers */}
        <div className="bg-white shadow-xl border border-gray-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Chargement des dossiers...</span>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Aucun dossier trouvé</p>
              <p className="text-sm">Les dossiers extraits apparaîtront ici</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fichier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date de création
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Véhicule
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Immatriculation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {records.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {record.original_filename}
                        </div>
                        <div className="text-sm text-gray-500">{record.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(record.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {record.marque || "N/A"}
                        </div>
                        <div className="text-sm text-gray-500">{record.type_vehicule || ""}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.immatriculation || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => setSelectedRecord(record)}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-xs font-medium"
                        >
                          <Eye className="w-3 h-3" />
                          Détails
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal de détails avec détection de fraude */}
        {selectedRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-6xl max-h-[90vh] overflow-y-auto w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-800">
                    Détails du Dossier
                  </h3>
                  <div className="flex gap-2">
                    <div className="flex items-center gap-2">
                      <select
                        value={fraudModelProvider}
                        onChange={(e) => setFraudModelProvider(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="gemini">Gemini</option>
                        <option value="qwen">Qwen</option>
                      </select>
                      <button
                        onClick={() => analyzeFraud(selectedRecord.id)}
                        disabled={fraudLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm font-medium"
                      >
                        <Shield className="w-4 h-4" />
                        {fraudLoading ? "Analyse en cours..." : "Détection de Fraude"}
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedRecord(null);
                        setFraudAnalysis(null);
                      }}
                      className="text-gray-500 hover:text-red-600 p-1 rounded-full hover:bg-red-50"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Analyse de fraude dans le modal */}
                {fraudAnalysis && (
                  <div className="mb-6 bg-white shadow-lg border border-gray-200 rounded-xl p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <Shield className="w-6 h-6 text-blue-600" />
                        Analyse de Fraude
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full ml-2">
                          {fraudAnalysis.model_provider === 'gemini' ? 'Gemini' : 'Qwen'}
                        </span>
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className={`p-4 rounded-lg ${getRiskColor(fraudAnalysis.analysis.fraud_risk_level)}`}>
                        <div className="text-sm font-medium">Niveau de Risque</div>
                        <div className="text-lg font-bold capitalize">{fraudAnalysis.analysis.fraud_risk_level}</div>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-sm font-medium text-blue-700">Score de Fraude</div>
                        <div className="text-lg font-bold text-blue-800">{fraudAnalysis.analysis.fraud_score}/100</div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="text-sm font-medium text-gray-700">Date d'Analyse</div>
                        <div className="text-sm text-gray-800">{formatDate(fraudAnalysis.timestamp)}</div>
                      </div>
                    </div>

                    {fraudAnalysis.analysis.indicators && fraudAnalysis.analysis.indicators.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-orange-500" />
                          Indicateurs de Risque
                        </h4>
                        <div className="space-y-2">
                          {fraudAnalysis.analysis.indicators.map((indicator, index) => (
                            <div
                              key={index}
                              className={`p-3 rounded-lg border-l-4 ${
                                indicator.severity === "élevé"
                                  ? "border-red-500 bg-red-50"
                                  : indicator.severity === "moyen"
                                  ? "border-orange-500 bg-orange-50"
                                  : "border-yellow-500 bg-yellow-50"
                              }`}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="font-medium text-gray-800">{indicator.type}</div>
                                  <div className="text-sm text-gray-600">{indicator.description}</div>
                                </div>
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                                    indicator.severity === "élevé"
                                      ? "bg-red-500 text-white"
                                      : indicator.severity === "moyen"
                                      ? "bg-orange-500 text-white"
                                      : "bg-yellow-500 text-white"
                                  }`}
                                >
                                  {indicator.severity}
                                </span>
                              </div>
                              {indicator.field && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Champ concerné: {indicator.field}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {fraudAnalysis.analysis.recommendations && fraudAnalysis.analysis.recommendations.length > 0 && (
                      <div>
                        <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                          <Info className="w-5 h-5 text-blue-500" />
                          Recommandations
                        </h4>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                          {fraudAnalysis.analysis.recommendations.map((rec, index) => (
                            <li key={index}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Détails du dossier */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(selectedRecord).map(([key, value]) => {
                    if (key === "id" || key === "created_at") return null;
                    
                    const label = getFieldLabel(key);
                    return (
                      <div key={key} className="p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm font-medium text-gray-700 mb-1">
                          {label}
                        </div>
                        <div className="text-gray-900 break-words">
                          {value || "N/A"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------- App -----------------
export default function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <main>
          <Routes>
            <Route path="/" element={<UploadPage />} />
            <Route path="/tables" element={<TableExtractionPage />} />
            <Route path="/records" element={<RecordsPage />} />
            <Route path="*" element={<UploadPage />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}